import { Form, Formik } from 'formik';
import InputText from '../../../components/input-text/inputText';
import ModalComponent from '../../../components/modal/modal';
import ModalBody from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import CustomTextArea from '../../../components/text-area/textArea';
import {
  usePostCommissionRateSchedule,
  usePostDepartment,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import { type ModalStatusType } from '../../../types/common';
import {
  COMMISSION_VALIDATION,
  DEPARTMENT_VALIDATION,
} from '../../../utils/validations/validations';
import _ from 'lodash';
import FormSelect from '../../../components/dropdown/formSelect';
import useGetBrokerOptions from '../../../hooks/get/useGetBrokerOptions';
import { CommaFormatted, CommaFormattedFloatPost } from '../../../utils/global';
import RateBracketComponent from './rateBracketComponent';

interface CommissionModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
  loading: boolean;
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
  modalStatus: ModalStatusType;
  id?: string;
}

const CommissionModal = ({
  isModalOpen,
  closeModal,
  initialData,
  setModalStatus,
  loading,
  handleRequest,
  modalStatus,
  id
}: CommissionModalType): JSX.Element => {
  const { handleRequest: customerHandleRequest } = useWealthlaneCustomerApi();

  const { mutate, isLoading } = usePostCommissionRateSchedule(
    handleRequest,
    closeModal,
    initialData.id
  );

  const { data: brokerOptions, isLoading: isBrokerOptionsLoading } =
    useGetBrokerOptions(customerHandleRequest);

  return (
    <Formik
      enableReinitialize
      initialValues={initialData.data}
      validationSchema={COMMISSION_VALIDATION}
      onSubmit={(values) => {
        const payload = {
          ...values,
          baseRate: Number(String(values?.baseRate)?.replaceAll(',', '') ?? ''),
          maximumAmount: Number(String(values?.maximumAmount)?.replaceAll(',', '') ?? ''),
          rateBrackets: values?.rateBrackets?.map((item: any) => ({
            lowerShareLimit: Number(String(item?.lowerShareLimit)?.replaceAll(',', '') ?? ''),
            rate: Number(String(item?.rate)?.replaceAll(',', '') ?? ''),
            // upperLimit: Number(String(item?.upperLimit)?.replaceAll(',', '') ?? ''),
          })),
        };
        mutate(payload);
      }}>
      {({
        touched,
        handleChange,
        errors,
        values,
        setFieldValue,
        setFieldTouched,
        setValues,
        setTouched,
      }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            loading={loading}
            size='xl'
            valueEdited={_.isEqual(values, initialData.data)}
            title={`${modalStatus} Commission`}
            id={id}
            // showToolTip={true}
            // tooltipText={`Permissions allow a company to control its users’ access levels to the module and its fields depending on their roles and functions. Users can have multiple levels of access such as creating and editing a module, to read-only or none at all.`}
            children={
              <Form id="commissionForm">
                <ModalBody id="modalBody">
                  <div className='grid grid-cols-3 gap-4' id="commissionFields">
                    <div className='col-span-2' id="nameSection">
                      <InputText
                        type='text'
                        label='Name'
                        name='name'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        required
                        id="commissionNameInput"
                      />
                    </div>
                  </div>
                  <FormSelect
                    label='Broker'
                    name='brokerCompanyId'
                    id='brokerCompanyId'
                    viewName='broker'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    options={brokerOptions}
                    placeholder='Select Type'
                    values={values}
                    inputType={modalStatus}
                    disabled={modalStatus === 'View'}
                    containerClassName='formGroup '
                    setFieldValue={setFieldValue}
                    width='360px'
                    isClearable={modalStatus !== 'View'}
                    required
                  />
                  <CustomTextArea
                    label='Description'
                    name='description'
                    id='description'
                    onChange={handleChange}
                    errors={errors}
                    inputType={modalStatus}
                    disabled={modalStatus === 'View'}
                    touched={touched}
                    rows='5'
                  />
                  <div className='grid grid-cols-2 gap-4' id="commissionRateFields">
                    <InputText
                      name={`baseRate`}
                      id={`baseRate`}
                      onChange={(e: any) => {
                        handleChange(e);
                        CommaFormattedFloatPost(e.target.value, `baseRate`, setFieldValue);
                      }}
                      // onFocus={(e: any) => {
                      //   values?.baseRate.replaceAll(',', '');
                      // }}
                      onBlur={(e: any) => {
                        CommaFormattedFloatPost(e.target.value, `baseRate`, setFieldValue);
                        setFieldTouched(`baseRate`, true);
                      }}
                      // errorValue={errors?.baseRate}
                      // touchedValue={touched?.baseRate}
                      label='Base Rate ($)'
                      touched={touched}
                      errors={errors}
                      type='text'
                      disabled={modalStatus === 'View'}
                      required
                    />
                    <InputText
                      name={`maximumAmount`}
                      id={`maximumAmount`}
                      onChange={(e: any) => {
                        handleChange(e);
                        CommaFormattedFloatPost(e.target.value, `maximumAmount`, setFieldValue);
                      }}
                      // onFocus={(e: any) => {
                      //   values?.maximumAmount.replaceAll(',', '');
                      // }}
                      onBlur={(e: any) => {
                        CommaFormattedFloatPost(e.target.value, `maximumAmount`, setFieldValue);
                        setFieldTouched(`maximumAmount`, true);
                      }}
                      // errorValue={errors?.maximumAmount}
                      // touchedValue={touched?.maximumAmount}
                      label='Maximum Amount ($)'
                      touched={touched}
                      errors={errors}
                      type='text'
                      disabled={modalStatus === 'View'}
                      required
                    />
                  </div>
                  {/* Rate Bracket */}
                  <RateBracketComponent
                    handleChange={handleChange}
                    errors={errors}
                    touched={touched}
                    values={values}
                    modalStatus={modalStatus}
                    setFieldValue={setFieldValue}
                    setValues={setValues}
                    setTouched={setTouched}
                    setFieldTouched={setFieldTouched}
                    id="commissionRateBracketComponent"
                  />
                </ModalBody>
                <ModalFooterComponent
                  id="commissionModalFooter"
                  modalStatus={modalStatus}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  valueChanged={_.isEqual(values, initialData.data)}
                  loading={isLoading}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default CommissionModal;
